// Call accept ONLY after the authenticated bot-open endpoint verifies exact initData.
export const INTENT_TTL = 30 * 60 * 1000;
const KEY = 'jungles.bot-intent.v1';
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type Intent = 'workout' | 'food' | 'notifications' | 'home' | 'nutrition' | 'results' | 'league' | `day_${string}`;
type RecordValue = { owner: string | null; launch: string; intent: Intent | null; expires: number };
export function parseStart(value: string | null): Intent | null {
  if (!value) return null;
  if (['workout', 'food', 'notifications', 'home', 'nutrition', 'results', 'league'].includes(value)) return value as Intent;
  if (!/^day_[1-9]\d{3}-\d{2}-\d{2}$/.test(value)) return null;
  const date = value.slice(4);
  const instant = new Date(`${date}T12:00:00Z`);
  return Number.isFinite(instant.getTime()) && instant.toISOString().slice(0, 10) === date ? value as Intent : null;
}
export class BotIntentStore {
  private memory: RecordValue | null = null;
  private storage?: Store;
  constructor(storage?: Store) { this.storage = storage; }
  private read(): RecordValue | null {
    let raw: string | null | undefined;
    try { raw = this.storage?.getItem(KEY); } catch { return this.memory; }
    if (!raw) return this.memory;
    try {
      const value = JSON.parse(raw) as RecordValue;
      if (value && typeof value.launch === 'string' && typeof value.expires === 'number' && Number.isFinite(value.expires) && (value.owner === null || typeof value.owner === 'string') && (value.intent === null || parseStart(value.intent))) this.memory = value;
      else this.memory = null;
    } catch { this.memory = null; }
    if (!this.memory) { try { this.storage?.removeItem(KEY); } catch { /* Invalid data stays rejected. */ } }
    return this.memory;
  }
  private write(value: RecordValue): void {
    this.memory = value;
    try { this.storage?.setItem(KEY, JSON.stringify(value)); } catch { /* Memory still prevents replay. */ }
  }
  accept(owner: string, raw: string, now = Date.now()): void {
    const params = new URLSearchParams(raw);
    if (['hash', 'start_param', 'auth_date', 'query_id'].some(key => params.getAll(key).length > 1)) { this.clear(); return; }
    if (!/^[a-fA-F0-9]{64}$/.test(params.get('hash') ?? '')) { this.clear(); return; }
    // No raw user data, credentials or permission claims are persisted.
    const launch = JSON.stringify(['hash', 'auth_date', 'query_id', 'start_param'].map(key => params.get(key)));
    const previous = this.read();
    if (previous?.launch === launch) { this.pending(owner, now); return; }
    this.write({ owner, launch, intent: parseStart(params.get('start_param')), expires: now + INTENT_TTL });
  }
  pending(owner: string, now = Date.now()): Intent | null {
    const value = this.read();
    if (!value) return null;
    if (value.owner !== owner || now >= value.expires || now < value.expires - INTENT_TTL) {
      this.write({ ...value, owner: null, intent: null }); return null;
    }
    return value.intent;
  }
  consume(owner: string, now = Date.now()): Intent | null {
    const intent = this.pending(owner, now);
    const value = this.read();
    if (value) this.write({ ...value, intent: null });
    return intent;
  }
  suppress(raw: string): void {
    this.accept('__suppressed__', raw);
    this.clear();
  }
  clear(): void {
    const value = this.read();
    if (value) this.write({ ...value, owner: null, intent: null });
  }
}
let storage: Store | undefined;
try { storage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage; } catch { /* Private mode. */ }
export const botIntents = new BotIntentStore(storage);
